import SvgIcon from '../SvgIcon/SvgIcon';
import './Dropdown.scss';
import MultiSelectOptionsList from './DropdownComponents/MultiSelectOptionsList/MultiSelectOptionsList';
import OptionsList from './DropdownComponents/OptionsList/OptionsList';
import useDropdown from '../../hooks/useDropdown/useDropdown';
import {
  getInputClassList,
  getInputIconColour,
  getInputWrapperClassList,
} from '../../utils/input/input';
import Text from '../Text/Text';

const Dropdown = ({
  inputId,
  label,
  value,
  onChange,
  handleClick = () => {},
  rightAligned = true,
  options,
  selectedOptions,
  placeholder = '',
  disabled = false,
  readOnly = false,
  error = '',
  tabIndex = 0,
  dropdownTabIndex = 0,
  multiSelect = false,
  small = false,
  additionalCssClass,
  defaultOption = false,
  leftAlignedDropdownLabels = false,
  dropUpwards = false,
  filteredDropdown = false,
  initiallyOpen = false,
  required = false,
}: {
  inputId: string;
  label?: string;
  value?: string;
  onChange: () => void;
  handleClick?: () => void;
  options: { label: string; value: string; disabled: boolean }[];
  rightAligned?: boolean;
  selectedOptions: string[];
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  tabIndex?: number;
  dropdownTabIndex?: number;
  multiSelect?: boolean;
  small?: boolean;
  additionalCssClass?: string;
  defaultOption?: boolean;
  leftAlignedDropdownLabels?: boolean;
  dropUpwards?: boolean;
  filteredDropdown?: boolean;
  initiallyOpen?: boolean;
  required?: boolean;
}) => {
  const { dropdownRef, dropdownOptionsListRef, isDropdownOpen, toggleDropdown } =
    useDropdown(initiallyOpen);

  const getOptionsList = () => {
    if (multiSelect) {
      return (
        <MultiSelectOptionsList
          inputId={inputId}
          options={options}
          selectedOptions={selectedOptions}
          isDropdownOpen={isDropdownOpen}
          rightAligned={rightAligned}
          onChange={onChange}
          dropdownTabIndex={dropdownTabIndex}
          ref={dropdownOptionsListRef}
        />
      );
    }

    return (
      <OptionsList
        inputId={inputId}
        options={options}
        isDropdownOpen={isDropdownOpen}
        toggleDropdown={toggleDropdown}
        rightAligned={rightAligned}
        onChange={onChange}
        dropdownTabIndex={dropdownTabIndex}
        ref={dropdownOptionsListRef}
        additionalCssClass={additionalCssClass}
        leftAlignedDropdownLabels={leftAlignedDropdownLabels}
        dropUpwards={dropUpwards}
        filteredDropdown={filteredDropdown}
      />
    );
  };

  return (
    <div
      data-testid={`${inputId}-dropdown`}
      className={`dropdown-container ${additionalCssClass}`}
      ref={dropdownRef}
    >
      {!!label && (
        <label htmlFor={inputId} className="body-small">
          <Text size="small" type="body">
            {`${label} ${required ? '*' : ''}`}
          </Text>
        </label>
      )}

      <div
        className={getInputWrapperClassList('custom-select body', readOnly, disabled, error)}
        id={`${inputId}-custom-select`}
      >
        <button
          id={`${inputId}-select-button`}
          data-testid={`${inputId}-select-button`}
          className={`${getInputClassList(
            placeholder,
            error,
            isDropdownOpen,
            additionalCssClass,
          )} ${small && 'smaller-height'}`}
          type="button"
          role="combobox"
          aria-labelledby={inputId}
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
          aria-controls="select-dropdown"
          onClick={() => {
            toggleDropdown();
            handleClick();
          }}
          disabled={disabled}
          tabIndex={tabIndex}
        >
          {value && !readOnly ? (
            <span
              data-testid="selected-dropdown-value"
              className={`selected-value body ${defaultOption && 'default-option-text'}`}
            >
              <Text type="body">{value}</Text>
            </span>
          ) : placeholder ? (
            <span className="selected-value body placeholder">
              <Text type="body">{`${placeholder}${required ? ' (required)' : ''}`}</Text>
            </span>
          ) : null}
          <span className="arrow">
            <SvgIcon
              iconName="ChevronDown"
              fill={getInputIconColour(disabled, readOnly)}
              width={12}
              height={12}
            />
          </span>
        </button>
        {!disabled && !readOnly && getOptionsList()}
      </div>
      {error && (
        <Text size="small" type="body" className="error-message">
          {error}
        </Text>
      )}
    </div>
  );
};

export default Dropdown;
